<template>
    <v-container fluid>
        <v-row dense>
            <v-col cols="12">
                <v-tabs left v-model="tabMain">
                    <v-tab
                        @click.native.prevent="changeTab('seafarer-profile')"
                        key="seafarer-profile"
                        href="#seafarer-profile"
                    >
                        {{ $t('tabs.profile') }}
                    </v-tab>
                    <v-tab
                        v-if="seafarer.id != 0 && hadStatus"
                        @click.native.prevent.stop="changeTab('activity')"
                        key="activity"
                        href="#activity"
                    >
                        {{ $t('tabs.activityLog') }}
                    </v-tab>
                    <v-tab
                        v-if="seafarer.id != 0"
                        @click.native.prevent.stop="changeTab('additional')"
                        key="additional"
                        href="#additional"
                    >
                        {{ $t('tabs.additional') }}
                    </v-tab>
                    <v-tab
                        v-if="seafarer.id != 0"
                        @click.native.prevent.stop="changeTab('navigation')"
                        key="navigation"
                        href="#navigation"
                    >
                        {{ $t('tabs.seaService') }}
                    </v-tab>
                    <v-tab
                        v-if="seafarer.id != 0"
                        @click.native.prevent.stop="changeTab('documents')"
                        key="documents"
                        href="#documents"
                    >
                        {{ $t('tabs.documents') }}
                    </v-tab>
                    <v-tab
                        v-if="seafarer.id != 0"
                        @click.native.prevent.stop="changeTab('archive')"
                        key="archive"
                        href="#archive"
                    >
                        {{ $t('tabs.documentArchive') }}
                    </v-tab>
                    <v-tab
                        v-if="seafarer.id != 0"
                        @click.native.prevent.stop="changeTab('placements')"
                        key="placements"
                        href="#placements"
                    >
                        {{ $t('tabs.placements') }}
                    </v-tab>
                    <v-tab
                        v-if="contractPlacementId != null"
                        @click.native.prevent.stop="changeTab('expenses')"
                        key="expenses"
                        href="#expenses"
                    >
                        {{ $t('tabs.expenses') }}
                    </v-tab>
                    <v-tab
                        v-if="contractPlacementId != null"
                        @click.native.prevent.stop="changeTab('salary')"
                        key="salary"
                        href="#salary"
                    >
                        {{ $t('tabs.salary') }}
                    </v-tab>
                </v-tabs>

                <v-tabs-items v-model="tab">
                    <!-- ── Profile tab ─────────────────────────────────────────────── -->
                    <v-tab-item key="seafarer-profile" value="seafarer-profile">
                        <v-row class="d-flex justify-space-between mt-1">
                            <!-- Left actions -->
                            <v-col class="d-flex justify-start">
                                <v-btn
                                    v-if="inEditMode && seafarer.id != 0"
                                    class="ml-2 mr-2"
                                    color="primary"
                                    @click="saveClick(false)"
                                >
                                    <v-icon small>fa-save</v-icon>&nbsp;{{ $t('buttons.save') }}
                                </v-btn>
                                <v-btn v-if="inEditMode" class="ml-2 mr-2" color="primary" @click="saveClick(true)">
                                    <v-icon small>fa-save</v-icon>&nbsp;{{ $t('buttons.saveAndExit') }}
                                </v-btn>
                                <v-btn
                                    v-if="!inEditMode && !seafarer.gdpr_edit_lock"
                                    class="ml-2 mr-2"
                                    color="primary"
                                    @click="editClick()"
                                >
                                    <v-icon small>fa-edit</v-icon>&nbsp;{{ $t('buttons.edit') }}
                                </v-btn>
                                <v-btn class="mr-2" @click="backClick">
                                    <v-icon small>fa-reply</v-icon>&nbsp;{{ $t('buttons.back') }}
                                </v-btn>
                            </v-col>

                            <!-- Image viewer -->
                            <v-col>
                                <ImageViewer
                                    :images="imageList"
                                    :toggler="lightboxShow"
                                    :index="lighboxIndex"
                                    @closeViewer="lightboxShow = false"
                                />
                            </v-col>

                            <!-- Right actions -->
                            <v-col class="d-flex justify-end mr-4">
                                <v-btn
                                    v-if="
                                        $route.params.id != 0 &&
                                        $myStore.state.user.user_role_id === 1 &&
                                        !seafarer.gdpr_edit_lock
                                    "
                                    color="warning"
                                    class="mr-2"
                                    :loading="gdprAnoning"
                                    @click="gdprAnonVerification()"
                                >
                                    <v-icon small>fas fa-file-pdf</v-icon>&nbsp;{{ $t('buttons.gdprAnonymization') }}
                                </v-btn>
                                <v-btn
                                    v-if="$route.params.id != 0 && !seafarer.gdpr_edit_lock"
                                    color="primary"
                                    :loading="exportingCv"
                                    @click="exportCV()"
                                >
                                    <v-icon small>fas fa-file-pdf</v-icon>&nbsp;{{ $t('buttons.exportCV') }}
                                </v-btn>
                            </v-col>
                        </v-row>

                        <v-row
                            style="
                                margin-top: 3px;
                                margin-bottom: 0;
                                height: 82vh;
                                overflow-y: scroll;
                                overflow-x: hidden;
                            "
                        >
                            <!-- ── Column 1: seafarer info + rating ─────────────────── -->
                            <v-col cols="4">
                                <v-card flat>
                                    <v-card-title>
                                        <v-row class="d-flex justify-space-between">
                                            <v-col class="ma-0 pa-0">{{ $t('labels.seafarerInformation') }}</v-col>
                                            <v-col class="mt-1 pa-0 d-flex justify-end">
                                                <CheckBox
                                                    class="ma-0 pa-0"
                                                    :readonly="approvedReadOnly"
                                                    v-model="seafarer.approved"
                                                    :label="$t('fields.approved')"
                                                />
                                            </v-col>
                                        </v-row>
                                    </v-card-title>
                                    <v-card-text>
                                        <v-row dense wrap>
                                            <!-- Photo: no photo uploaded -->
                                            <v-col v-if="seafarer.photo.upload_id == null">
                                                <v-col class="text-center pa-0 ma-0">
                                                    <img :src="publicPath + 'dummy_profile.jpg'" height="370px" />
                                                </v-col>
                                                <v-col v-if="inEditMode">
                                                    <UploadImage
                                                        v-model="seafarer.photo"
                                                        :error-border="!seafarerValidation.photo"
                                                        @click="lightboxShow = true"
                                                    />
                                                </v-col>
                                            </v-col>

                                            <!-- Photo: uploaded, no planned placement -->
                                            <v-col
                                                v-if="seafarer.photo.upload_id != null && plannedPlacementId == null"
                                                cols="12"
                                                class="d-flex justify-left photoNoPlanned"
                                            >
                                                <UploadImage
                                                    :readonly="!inEditMode"
                                                    v-model="seafarer.photo"
                                                    :error-border="!seafarerValidation.photo"
                                                    @click="lightboxShow = true"
                                                />
                                            </v-col>

                                            <!-- Photo: uploaded, with planned placement -->
                                            <v-col
                                                v-if="seafarer.photo.upload_id != null && plannedPlacementId != null"
                                                cols="12"
                                                class="d-flex justify-left photoPlanned"
                                            >
                                                <UploadImage
                                                    :readonly="!inEditMode"
                                                    v-model="seafarer.photo"
                                                    :error-border="!seafarerValidation.photo"
                                                    @click="lightboxShow = true"
                                                />
                                            </v-col>

                                            <!-- Status (no contract) -->
                                            <v-col :cols="contractPlacementId == null ? 12 : 11">
                                                <Dropdown
                                                    :readonly="statusReadOnly"
                                                    v-model="seafarer.status_id"
                                                    :ajax="false"
                                                    :data="availableStatuses"
                                                    :error-border="!seafarerValidation.status_id"
                                                    :label="statusLabel"
                                                />
                                            </v-col>

                                            <!-- Contract placement link icon -->
                                            <v-col cols="1" v-if="contractPlacementId != null">
                                                <v-tooltip bottom>
                                                    <template v-slot:activator="{on, attrs}">
                                                        <v-btn
                                                            color="primary"
                                                            icon
                                                            v-bind="attrs"
                                                            v-on="on"
                                                            @click="openContractPlacement()"
                                                        >
                                                            <v-icon small>fas fa-link</v-icon>
                                                        </v-btn>
                                                    </template>
                                                    <span>{{ $t('tooltips.openPlacement') }}</span>
                                                </v-tooltip>
                                            </v-col>

                                            <!-- Planned date -->
                                            <v-col cols="11" v-if="plannedPlacementId != null">
                                                <DatePicker
                                                    :readonly="true"
                                                    v-model="seafarer.planned_date"
                                                    :error-border="!seafarerValidation.planned_date"
                                                    :date-label="$t('fields.plannedDate')"
                                                />
                                            </v-col>

                                            <!-- Planned placement link icon -->
                                            <v-col cols="1" v-if="plannedPlacementId != null">
                                                <v-tooltip bottom>
                                                    <template v-slot:activator="{on, attrs}">
                                                        <v-btn
                                                            color="primary"
                                                            icon
                                                            v-bind="attrs"
                                                            v-on="on"
                                                            @click="openPlacement()"
                                                        >
                                                            <v-icon small>fas fa-link</v-icon>
                                                        </v-btn>
                                                    </template>
                                                    <span>{{ $t('tooltips.openPlacement') }}</span>
                                                </v-tooltip>
                                            </v-col>

                                            <v-col cols="12">
                                                <Dropdown
                                                    :readonly="!inEditMode"
                                                    v-model="seafarer.rank_id"
                                                    :ajax="true"
                                                    :data="'ranks'"
                                                    :error-border="!seafarerValidation.rank_id"
                                                    :label="rankLabel"
                                                />
                                            </v-col>
                                            <v-col cols="12">
                                                <InputField
                                                    :readonly="!inEditMode"
                                                    :maxlength="50"
                                                    v-model="seafarer.seafarer_book"
                                                    :error-border="!seafarerValidation.seafarer_book"
                                                    :label="$t('fields.seamanBook')"
                                                />
                                            </v-col>
                                            <v-col cols="12">
                                                <DatePicker
                                                    :readonly="!inEditMode"
                                                    v-model="seafarer.with_azara_from"
                                                    :error-border="!seafarerValidation.with_azara_from"
                                                    :date-label="$t('fields.withAzaraFrom')"
                                                />
                                            </v-col>
                                            <v-col cols="12">
                                                <DatePicker
                                                    :readonly="!inEditMode"
                                                    v-model="seafarer.available_date"
                                                    :date-label="$t('fields.availableDate')"
                                                />
                                            </v-col>
                                        </v-row>
                                    </v-card-text>
                                </v-card>

                                <v-card flat>
                                    <v-card-title>{{ $t('labels.rating') }}</v-card-title>
                                    <v-card-text>
                                        <v-row dense wrap>
                                            <v-col cols="4">
                                                <p class="font-weight-bold text-h6 mt-2">
                                                    {{ $t('fields.perfomance') }}
                                                </p>
                                            </v-col>
                                            <v-col cols="8">
                                                <v-rating
                                                    v-model="seafarer.perfomance"
                                                    color="primary"
                                                    background-color="grey darken-1"
                                                    hover
                                                    medium
                                                />
                                            </v-col>
                                            <v-col cols="4">
                                                <p class="font-weight-bold text-h6 mt-2">{{ $t('fields.attitude') }}</p>
                                            </v-col>
                                            <v-col cols="8">
                                                <v-rating
                                                    v-model="seafarer.attitude"
                                                    color="primary"
                                                    background-color="grey darken-1"
                                                    hover
                                                    medium
                                                />
                                            </v-col>
                                            <v-col cols="4" class="d-flex-nowrap">
                                                <p class="font-weight-bold text-h6 mt-2">
                                                    {{ $t('fields.communication') }}
                                                </p>
                                            </v-col>
                                            <v-col cols="8">
                                                <v-rating
                                                    v-model="seafarer.communication"
                                                    color="primary"
                                                    background-color="grey darken-1"
                                                    hover
                                                    medium
                                                />
                                            </v-col>
                                            <v-col cols="4">
                                                <p class="font-weight-bold text-h6 mt-2">{{ $t('fields.overall') }}</p>
                                            </v-col>
                                            <v-col cols="8">
                                                <v-rating
                                                    v-model="seafarer.overall"
                                                    color="primary"
                                                    background-color="grey darken-1"
                                                    hover
                                                    medium
                                                    readonly
                                                    half-increments
                                                />
                                            </v-col>
                                        </v-row>
                                    </v-card-text>
                                </v-card>
                            </v-col>

                            <!-- ── Column 2: personal info + bank ──────────────────── -->
                            <v-col cols="4">
                                <v-card flat>
                                    <v-card-title class="pt-1 mt-0">{{
                                        $t('labels.personalInformation')
                                    }}</v-card-title>
                                    <v-card-text>
                                        <v-row dense wrap>
                                            <!-- GDPR -->
                                            <v-col class="ma-0 pa-0">
                                                <v-row class="ma-0 pa-0">
                                                    <v-col cols="3" class="ma-0 pa-0 pt-3 pb-3">
                                                        <CheckBox
                                                            class="ma-0 pa-0"
                                                            :readonly="!inEditMode"
                                                            v-model="seafarer.gdpr"
                                                            :label="$t('fields.gdpr')"
                                                        />
                                                    </v-col>
                                                    <v-col cols="9" class="ma-0 pa-0 pr-1 pt-1" v-if="seafarer.gdpr">
                                                        <Upload
                                                            class="ma-0 pa-0"
                                                            :readonly="!inEditMode"
                                                            v-model="seafarer.gdpr_photo"
                                                            :error-border="!seafarerValidation.gdpr_photo"
                                                        />
                                                    </v-col>
                                                    <v-col v-else cols="9" class="ma-0 pa-2" />
                                                </v-row>
                                            </v-col>

                                            <v-col cols="12" v-if="seafarer.id != 0">
                                                <InputField
                                                    :readonly="true"
                                                    v-model="seafarer.id"
                                                    :label="$t('fields.id')"
                                                />
                                            </v-col>
                                            <v-col cols="12">
                                                <InputField
                                                    :readonly="!inEditMode"
                                                    :maxlength="50"
                                                    v-model="seafarer.name"
                                                    :error-border="!seafarerValidation.name"
                                                    :label="'* ' + $t('fields.firstName')"
                                                />
                                            </v-col>
                                            <v-col cols="12">
                                                <InputField
                                                    :readonly="!inEditMode"
                                                    :maxlength="50"
                                                    v-model="seafarer.surname"
                                                    :error-border="!seafarerValidation.surname"
                                                    :label="'* ' + $t('fields.lastName')"
                                                />
                                            </v-col>
                                            <v-col cols="12">
                                                <InputField
                                                    :readonly="!inEditMode"
                                                    :maxlength="50"
                                                    v-model="seafarer.middle_name"
                                                    :error-border="!seafarerValidation.middle_name"
                                                    :label="$t('fields.middleName')"
                                                />
                                            </v-col>
                                            <v-col cols="8">
                                                <DatePicker
                                                    :readonly="!inEditMode"
                                                    v-model="seafarer.date_of_birth"
                                                    :error-border="!seafarerValidation.date_of_birth"
                                                    :date-label="'* ' + $t('fields.dateOfBirth')"
                                                />
                                            </v-col>
                                            <v-col cols="4">
                                                <InputField :readonly="true" v-model="age" :label="$t('fields.age')" />
                                            </v-col>
                                            <v-col cols="12">
                                                <Dropdown2
                                                    :readonly="!inEditMode"
                                                    v-model="seafarer.nationality_id"
                                                    :ajax="true"
                                                    :data="'nationalities'"
                                                    :error-border="!seafarerValidation.nationality_id"
                                                    :label="'* ' + $t('fields.nationality')"
                                                />
                                            </v-col>
                                            <v-col cols="12">
                                                <InputField
                                                    :readonly="oibReadOnly"
                                                    :maxlength="11"
                                                    v-model="seafarer.oib"
                                                    :error-border="!seafarerValidation.oib"
                                                    :label="$t('fields.nationalIdentifierOIB')"
                                                />
                                            </v-col>
                                            <v-col cols="12">
                                                <Dropdown2
                                                    :readonly="!inEditMode"
                                                    v-model="seafarer.country_of_residency_id"
                                                    :ajax="true"
                                                    :data="'countries'"
                                                    :error-border="!seafarerValidation.country_of_residency_id"
                                                    :label="'* ' + $t('fields.countryOfResidency')"
                                                />
                                            </v-col>
                                            <v-col cols="12">
                                                <InputField
                                                    :readonly="!inEditMode"
                                                    :maxlength="50"
                                                    v-model="seafarer.place_of_birth"
                                                    :error-border="!seafarerValidation.place_of_birth"
                                                    :label="'* ' + $t('fields.placeOfBirth')"
                                                />
                                            </v-col>
                                            <v-col cols="12">
                                                <Dropdown
                                                    :readonly="!inEditMode"
                                                    v-model="seafarer.gender_id"
                                                    :ajax="true"
                                                    :data="'genders'"
                                                    :error-border="!seafarerValidation.gender_id"
                                                    :label="'* ' + $t('fields.gender')"
                                                />
                                            </v-col>
                                            <v-col cols="12">
                                                <Dropdown
                                                    :readonly="!inEditMode"
                                                    v-model="seafarer.marital_status_id"
                                                    :ajax="true"
                                                    :data="'marital-statuses'"
                                                    :error-border="!seafarerValidation.marital_status_id"
                                                    :label="'* ' + $t('fields.maritalStatus')"
                                                />
                                            </v-col>
                                        </v-row>
                                    </v-card-text>
                                </v-card>

                                <v-card flat>
                                    <v-card-title>{{ $t('labels.bankAccountDetails') }}</v-card-title>
                                    <v-card-text>
                                        <v-row dense wrap>
                                            <v-col cols="12">
                                                <v-row dense>
                                                    <v-col :cols="bankDropdownCols" class="pr-2">
                                                        <Dropdown
                                                            :readonly="!inEditMode"
                                                            v-model="seafarer.bank_id"
                                                            :ajax="true"
                                                            :clearable="true"
                                                            :data="'banks'"
                                                            :error-border="!seafarerValidation.bank_id"
                                                            :label="$t('fields.bank')"
                                                            @changes="handleBankChange"
                                                        />
                                                    </v-col>
                                                    <v-col>
                                                        <v-btn block @click="syncBank" :disabled="!inEditMode">
                                                            <v-icon small>fa-sync-alt</v-icon>&nbsp;{{
                                                                $t('buttons.sync')
                                                            }}
                                                        </v-btn>
                                                    </v-col>
                                                </v-row>
                                            </v-col>
                                            <v-col cols="12">
                                                <InputField
                                                    :readonly="true"
                                                    :maxlength="50"
                                                    v-model="seafarer.bank_name"
                                                    :error-border="!seafarerValidation.bank_name"
                                                    :label="bankNameLabel"
                                                />
                                            </v-col>
                                            <v-row dense>
                                                <v-col cols="6" class="pl-2">
                                                    <InputField
                                                        :readonly="true"
                                                        :maxlength="50"
                                                        v-model="seafarer.bank_address"
                                                        :error-border="!seafarerValidation.bank_address"
                                                        :label="bankAddressLabel"
                                                    />
                                                </v-col>
                                                <v-col cols="6" class="pr-2">
                                                    <InputField
                                                        :readonly="true"
                                                        :maxlength="50"
                                                        v-model="seafarer.bank_town"
                                                        :error-border="!seafarerValidation.bank_town"
                                                        :label="bankCityLabel"
                                                    />
                                                </v-col>
                                            </v-row>
                                            <v-row dense>
                                                <v-col cols="6" class="pl-2">
                                                    <InputField
                                                        :readonly="true"
                                                        :maxlength="50"
                                                        v-model="seafarer.bank_post_code"
                                                        :error-border="!seafarerValidation.bank_post_code"
                                                        :label="bankPostCodeLabel"
                                                    />
                                                </v-col>
                                                <v-col cols="6" class="pr-2">
                                                    <Dropdown2
                                                        :readonly="true"
                                                        v-model="seafarer.bank_country_id"
                                                        :ajax="true"
                                                        :data="'countries'"
                                                        :error-border="!seafarerValidation.bank_country_id"
                                                        :label="bankCountryLabel"
                                                    />
                                                </v-col>
                                            </v-row>
                                            <v-row dense>
                                                <v-col cols="6" class="pl-2">
                                                    <InputField
                                                        :readonly="true"
                                                        :maxlength="11"
                                                        v-model="seafarer.swift_code"
                                                        :error-border="!seafarerValidation.swift_code"
                                                        :label="swiftCodeLabel"
                                                    />
                                                </v-col>
                                                <v-col cols="6" class="pr-2">
                                                    <Dropdown
                                                        :readonly="!inEditMode"
                                                        v-model="seafarer.account_currency_id"
                                                        :ajax="true"
                                                        :data="'currencies-with-exchange'"
                                                        :error-border="!seafarerValidation.account_currency_id"
                                                        :label="accountCurrencyLabel"
                                                    />
                                                </v-col>
                                            </v-row>
                                            <v-col cols="12">
                                                <InputField
                                                    :readonly="true"
                                                    v-model="seafarer.bank_acccount_holder_name"
                                                    :error-border="!seafarerValidation.bank_acccount_holder_name"
                                                    :label="$t('fields.accountHolderSName')"
                                                />
                                            </v-col>
                                            <v-col cols="12">
                                                <InputField
                                                    :readonly="!inEditMode"
                                                    :maxlength="34"
                                                    v-model="seafarer.iban"
                                                    :error-border="!seafarerValidation.iban"
                                                    :label="ibanLabel"
                                                />
                                            </v-col>
                                        </v-row>
                                    </v-card-text>
                                </v-card>
                            </v-col>

                            <!-- ── Column 3: address + NOK ─────────────────────────── -->
                            <v-col cols="4">
                                <v-card flat>
                                    <v-card-title class="pt-1 mt-0">{{
                                        $t('labels.addressAndContactInfo')
                                    }}</v-card-title>
                                    <v-card-text>
                                        <v-row dense wrap>
                                            <v-col cols="12">
                                                <InputField
                                                    :readonly="!inEditMode"
                                                    :maxlength="50"
                                                    v-model="seafarer.email"
                                                    :error-border="!seafarerValidation.email"
                                                    :label="'* ' + $t('fields.email')"
                                                    @blur="checkEmailExistance"
                                                />
                                            </v-col>
                                            <v-col cols="12">
                                                <InputField
                                                    :readonly="!inEditMode"
                                                    :maxlength="50"
                                                    v-model="seafarer.phone"
                                                    :error-border="!seafarerValidation.phone"
                                                    :label="'* ' + $t('fields.phone')"
                                                />
                                            </v-col>
                                            <v-col cols="12">
                                                <InputField
                                                    :readonly="!inEditMode"
                                                    :maxlength="50"
                                                    v-model="seafarer.street_name_and_number"
                                                    :error-border="!seafarerValidation.street_name_and_number"
                                                    :label="'* ' + $t('fields.streetNameAndNumber')"
                                                />
                                            </v-col>
                                            <v-col cols="12">
                                                <InputField
                                                    :readonly="!inEditMode"
                                                    :maxlength="50"
                                                    v-model="seafarer.city"
                                                    :error-border="!seafarerValidation.city"
                                                    :label="'* ' + $t('fields.city')"
                                                />
                                            </v-col>
                                            <v-col cols="12">
                                                <InputField
                                                    :readonly="!inEditMode"
                                                    :maxlength="10"
                                                    v-model="seafarer.post_code"
                                                    :error-border="!seafarerValidation.post_code"
                                                    :label="'* ' + $t('fields.postalCode')"
                                                />
                                            </v-col>
                                            <v-col cols="12">
                                                <Dropdown2
                                                    :readonly="!inEditMode"
                                                    v-model="seafarer.country_id"
                                                    :ajax="true"
                                                    :data="'countries'"
                                                    :error-border="!seafarerValidation.country_id"
                                                    :label="'* ' + $t('fields.country')"
                                                />
                                            </v-col>
                                            <v-col cols="12">
                                                <Dropdown
                                                    :readonly="!inEditMode"
                                                    v-model="seafarer.airport_id"
                                                    :ajax="true"
                                                    :data="'airports'"
                                                    :error-border="!seafarerValidation.airport_id"
                                                    :label="'* ' + $t('fields.closestIntAirport')"
                                                />
                                            </v-col>
                                        </v-row>
                                    </v-card-text>
                                </v-card>

                                <v-card flat>
                                    <v-card-title>
                                        <v-row dense class="d-flex justify-space-between">
                                            <v-col>{{ $t('labels.nextOfKinNOK') }}</v-col>
                                            <v-col class="d-flex justify-end">
                                                <v-btn @click="copyFromAddresClick" :disabled="!inEditMode">
                                                    <v-icon small>fa-copy</v-icon>&nbsp;{{
                                                        $t('buttons.copyFromAddress')
                                                    }}
                                                </v-btn>
                                            </v-col>
                                        </v-row>
                                    </v-card-title>
                                    <v-card-text>
                                        <v-row dense wrap>
                                            <v-col cols="12">
                                                <InputField
                                                    :readonly="!inEditMode"
                                                    :maxlength="50"
                                                    v-model="seafarer.emergency_contact_name"
                                                    :error-border="!seafarerValidation.emergency_contact_name"
                                                    :label="nameLabel"
                                                />
                                            </v-col>
                                            <v-col cols="12">
                                                <Dropdown
                                                    :readonly="!inEditMode"
                                                    v-model="seafarer.emergency_contact_relationship_id"
                                                    :ajax="true"
                                                    :data="'emergency-contact-relationship'"
                                                    :error-border="
                                                        !seafarerValidation.emergency_contact_relationship_id
                                                    "
                                                    :label="relationshipLabel"
                                                />
                                            </v-col>
                                            <v-col cols="12">
                                                <InputField
                                                    :readonly="!inEditMode"
                                                    :maxlength="50"
                                                    v-model="seafarer.emergency_contact_street_name_and_number"
                                                    :error-border="
                                                        !seafarerValidation.emergency_contact_street_name_and_number
                                                    "
                                                    :label="'* ' + $t('fields.streetNameAndNumber')"
                                                />
                                            </v-col>
                                            <v-col cols="12">
                                                <InputField
                                                    :readonly="!inEditMode"
                                                    :maxlength="50"
                                                    v-model="seafarer.emergency_contact_city"
                                                    :error-border="!seafarerValidation.emergency_contact_city"
                                                    :label="'* ' + $t('fields.city')"
                                                />
                                            </v-col>
                                            <v-col cols="12">
                                                <InputField
                                                    :readonly="!inEditMode"
                                                    :maxlength="10"
                                                    v-model="seafarer.emergency_contact_post_code"
                                                    :error-border="!seafarerValidation.emergency_contact_post_code"
                                                    :label="'* ' + $t('fields.postalCode')"
                                                />
                                            </v-col>
                                            <v-col cols="12">
                                                <Dropdown2
                                                    :readonly="!inEditMode"
                                                    v-model="seafarer.emergency_contact_country_id"
                                                    :ajax="true"
                                                    :data="'countries'"
                                                    :error-border="!seafarerValidation.emergency_contact_country_id"
                                                    :label="countryLabel"
                                                />
                                            </v-col>
                                            <v-col cols="12">
                                                <InputField
                                                    :readonly="!inEditMode"
                                                    :maxlength="50"
                                                    v-model="seafarer.contact_number"
                                                    :error-border="!seafarerValidation.contact_number"
                                                    :label="contactNumberLabel"
                                                />
                                            </v-col>
                                            <v-col cols="12">
                                                <InputField
                                                    :readonly="!inEditMode"
                                                    :maxlength="50"
                                                    v-model="seafarer.contact_email"
                                                    :error-border="!seafarerValidation.contact_email"
                                                    :label="contactEmailLabel"
                                                />
                                            </v-col>
                                            <v-col cols="12">
                                                <TextArea
                                                    :readonly="!inEditMode"
                                                    :maxlength="1000"
                                                    v-model="seafarer.notes"
                                                    :error-border="!seafarerValidation.notes"
                                                    :label="$t('fields.notes')"
                                                />
                                            </v-col>
                                        </v-row>
                                    </v-card-text>
                                </v-card>
                            </v-col>

                            <!-- ── Activity mini-table (approved seafarers only) ─────── -->
                            <v-col cols="12" v-if="seafarer.approved && seafarer.id != 0">
                                <v-card flat outlined>
                                    <v-card-text>
                                        <v-row>
                                            <v-col>
                                                <TheTable
                                                    :default-sort="activitiesTable.defaultSort"
                                                    :fields="activitiesTable.headers"
                                                    :header-selection-key="'seafarers-activity-profile-t'"
                                                    :height="activitiesTable.height"
                                                    :total-records="activitiesTable.total"
                                                    :filter-save-key="'seafarers-activity-filter-profile-t'"
                                                    :show-icons="true"
                                                    :show-footer="true"
                                                    @headers="selectHeaders"
                                                    @reload="reloadData"
                                                >
                                                    <!-- BUG FIX: was `props.item.id` (props undefined here), contextmenu
                                                         removed entirely — this is a read-only table on the profile tab,
                                                         editing is done via the dedicated Activity tab. -->
                                                    <tr v-for="(item, key) in activitiesTable.items" :key="key">
                                                        <!-- Status chip -->
                                                        <td
                                                            v-if="activitiesTable.selectedHeaders.includes('status')"
                                                            class="font-weight-bold"
                                                        >
                                                            <v-chip
                                                                class="text-uppercase pl-4"
                                                                :text-color="
                                                                    statusStyle(item.seafarer_status_id).textColor
                                                                "
                                                                :color="statusStyle(item.seafarer_status_id).color"
                                                            >
                                                                <v-icon
                                                                    x-small
                                                                    left
                                                                    :color="
                                                                        statusStyle(item.seafarer_status_id).textColor
                                                                    "
                                                                    >fas fa-circle</v-icon
                                                                >
                                                                {{ item.status_display }}
                                                            </v-chip>
                                                        </td>
                                                        <td
                                                            v-if="
                                                                activitiesTable.selectedHeaders.includes('start_date')
                                                            "
                                                            class="font-weight-bold"
                                                        >
                                                            {{ item.start_date_display }}
                                                        </td>
                                                        <td v-if="activitiesTable.selectedHeaders.includes('end_date')">
                                                            {{ item.end_date_display }}
                                                        </td>
                                                        <td
                                                            v-if="activitiesTable.selectedHeaders.includes('duration')"
                                                            class="font-weight-bold"
                                                        >
                                                            {{ item.duration }}
                                                        </td>
                                                        <td
                                                            v-if="activitiesTable.selectedHeaders.includes('day_type')"
                                                            class="font-weight-bold"
                                                        >
                                                            {{ item.day_type }}
                                                        </td>
                                                        <td
                                                            v-if="activitiesTable.selectedHeaders.includes('vessel')"
                                                            class="font-weight-bold"
                                                        >
                                                            {{ item.vessel_display }}
                                                        </td>
                                                        <td v-if="activitiesTable.selectedHeaders.includes('rank')">
                                                            {{ item.rank_info }}
                                                        </td>
                                                        <td v-if="activitiesTable.selectedHeaders.includes('entity')">
                                                            {{ item.entity_name }}
                                                        </td>
                                                        <td
                                                            v-if="activitiesTable.selectedHeaders.includes('placement')"
                                                        >
                                                            {{ item.placement_info }}&nbsp;
                                                            <v-tooltip bottom v-if="item.placement_id != null">
                                                                <template v-slot:activator="{on, attrs}">
                                                                    <v-icon
                                                                        v-bind="attrs"
                                                                        v-on="on"
                                                                        small
                                                                        @click="
                                                                            $router.push(
                                                                                '/placements/' + item.placement_id,
                                                                            )
                                                                        "
                                                                        >fas fa-link</v-icon
                                                                    >
                                                                </template>
                                                                <span>{{ $t('tooltips.openPlacement') }}</span>
                                                            </v-tooltip>
                                                        </td>
                                                        <td v-if="activitiesTable.selectedHeaders.includes('contract')">
                                                            {{ item.contract_display }}
                                                        </td>
                                                        <td v-if="activitiesTable.selectedHeaders.includes('note')">
                                                            {{ item.note }}
                                                        </td>
                                                        <td
                                                            v-if="
                                                                activitiesTable.selectedHeaders.includes('modified_by')
                                                            "
                                                        >
                                                            {{ item.must_edit ? '' : item.modified_by }}
                                                        </td>
                                                    </tr>
                                                </TheTable>
                                            </v-col>
                                        </v-row>
                                    </v-card-text>
                                </v-card>
                            </v-col>
                        </v-row>
                    </v-tab-item>

                    <!-- ── Other tabs ──────────────────────────────────────────────── -->
                    <v-tab-item key="activity" value="activity">
                        <SeafarerActivity
                            :seafarer="seafarer"
                            :in-edit-mode="inEditMode"
                            @edit="editForTab()"
                            @reloadSeafarer="reloadSeafarer()"
                        />
                    </v-tab-item>
                    <v-tab-item key="additional" value="additional">
                        <SeafarerAdditional
                            :reload="tabMain"
                            :seafarer="seafarer"
                            :in-edit-mode="inEditMode"
                            @edit="editForTab()"
                            @dirty="additionalDirty = $event"
                        />
                    </v-tab-item>
                    <v-tab-item key="navigation" value="navigation">
                        <SeafarerNavigation
                            :reload="reload"
                            :seafarer="seafarer"
                            :in-edit-mode="inEditMode"
                            @edit="editForTab()"
                        />
                    </v-tab-item>
                    <v-tab-item key="documents" value="documents">
                        <SeafarerDocuments
                            :reload="currentTab"
                            :seafarer="seafarer"
                            :in-edit-mode="inEditMode"
                            @edit="inEditMode = !inEditMode"
                        />
                    </v-tab-item>
                    <v-tab-item
                        key="archive"
                        value="archive"
                        style="margin-top: 3px; height: 92vh; overflow-y: scroll; overflow-x: hidden"
                    >
                        <SeafarerDocumentsArchive
                            :reload="currentTab"
                            :seafarer="seafarer"
                            :in-edit-mode="inEditMode"
                            @edit="inEditMode = !inEditMode"
                        />
                    </v-tab-item>
                    <v-tab-item
                        key="placements"
                        value="placements"
                        style="margin-top: 3px; height: 92vh; overflow-y: scroll; overflow-x: hidden"
                    >
                        <SeafarerPlacements :seafarer="seafarer" />
                        <SeafarerApplications :seafarer="seafarer" />
                    </v-tab-item>
                    <v-tab-item
                        key="expenses"
                        value="expenses"
                        style="margin-top: 3px; height: 92vh; overflow-y: scroll; overflow-x: hidden"
                    >
                        <SeafarerExpensesTab
                            :placement-id="contractPlacementId"
                            :in-edit-mode="inEditMode"
                            @edit="editForTab()"
                        />
                    </v-tab-item>
                    <v-tab-item
                        key="salary"
                        value="salary"
                        style="margin-top: 3px; height: 92vh; overflow-y: scroll; overflow-x: hidden"
                    >
                        <SeafarerSalaryTab
                            :reload="currentTab"
                            :placement-id="contractPlacementId"
                            :in-edit-mode="inEditMode"
                            @edit="editForTab()"
                        />
                    </v-tab-item>
                </v-tabs-items>
            </v-col>
        </v-row>

        <!-- GDPR OTP dialog -->
        <v-row>
            <v-col>
                <v-dialog v-model="showOtpModal" width="auto" :max-width="400">
                    <v-card class="py-8 px-6 text-center mx-auto ma-4" elevation="12">
                        <h3 class="text-h6 mb-4">{{ $t('labels.verifyGdprAnonymization') }}</h3>
                        <div class="text-body-2">
                            {{ $t('labels.pasteCode') }} <b>{{ otp }}</b>
                        </div>
                        <v-otp-input v-model="enteredOtp" :length="4" />
                        <v-btn
                            class="my-4"
                            color="primary"
                            width="70%"
                            :disabled="verifyingGdprIsActive"
                            :loading="gdprAnoning"
                            @click="gdprAnonExecution()"
                        >
                            {{ $t('buttons.verify') }}
                        </v-btn>
                        <div class="text-caption">
                            {{ $t('labels.dontSeeCode') }}
                            <a href="#" style="text-decoration: none" @click.prevent="generateOtp()">{{
                                $t('buttons.regenerateCode')
                            }}</a>
                        </div>
                    </v-card>
                </v-dialog>
            </v-col>
        </v-row>
    </v-container>
</template>

<script>
import InputField from './trinkets/InputField';
import CheckBox from './trinkets/CheckBox';
import Dropdown from './trinkets/Dropdown';
import Dropdown2 from './trinkets/Dropdown2';
import Seafarer from '../models/Seafarer';
import DatePicker from '@/components/trinkets/DatePicker';
import Upload from '@/components/trinkets/Upload';
import UploadImage from '@/components/trinkets/ProfilePicture';
import TextArea from '@/components/trinkets/TextArea';
import SeafarerAdditional from '@/components/SeafarerAdditional';
import SeafarerNavigation from './SeafarerNavigation';
import SeafarerDocuments from './SeafarerDocuments';
import SeafarerDocumentsArchive from './SeafarerDocumentsArchive';
import SeafarerPlacements from './SeafarerPlacements';
import SeafarerApplications from './SeafarerApplications';
import SeafarerActivity from './SeafarerActivity.vue';
import TheTable from './trinkets/TheTable';
import ImageViewer from './trinkets/ImageViewer';
import SeafarerExpensesTab from './SeafarerExpensesTab.vue';
import SeafarerSalaryTab from './SeafarerSalaryTab.vue';

// Shared with SeafarerActivity — status chip colour config
const STATUS_STYLES = {
    1: {textColor: '#212528', color: '#a3b8c9'},
    2: {textColor: '#ac8ea9', color: '#f5cbf1'},
    3: {textColor: '#008000', color: '#00800040'},
    4: {textColor: '#000000', color: '#ffffff'},
    5: {textColor: '#BBBB00', color: '#E1E10040'},
    6: {textColor: '#ac8ea9', color: '#f5cbf1'},
    7: {textColor: '#BBBB00', color: '#E1E10040'},
    8: {textColor: '#49707c', color: '#92e0f7'},
    9: {textColor: '#BBBB00', color: '#E1E10040'},
};

// Lock key per tab — centralised so changeTab() doesn't need a switch
const TAB_LOCK_KEYS = {
    'seafarer-profile': 'seafarer-profile',
    'activity': 'activity',
    'additional': 'additional',
    'navigation': 'navigation',
    'documents': 'documents',
    'archive': 'archive',
    'placements': 'placements',
    'expenses': 'expenses',
    'salary': 'salary',
};

// Nationality ID for Croatia (OIB required)
const NATIONALITY_HR_ID = 56;

// Required fields for the NOK / bank section when seafarer is approved
const APPROVED_REQUIRED_STRING_FIELDS = [
    {field: 'emergency_contact_name', label: 'fields.name'},
    {field: 'emergency_contact_street_name_and_number', label: 'fields.streetNameAndNumber'},
    {field: 'emergency_contact_city', label: 'fields.city'},
    {field: 'emergency_contact_post_code', label: 'fields.postalCode'},
    {field: 'contact_number', label: 'fields.contactNumber'},
];
const APPROVED_REQUIRED_ID_FIELDS = [
    {field: 'emergency_contact_relationship_id', label: 'fields.relationship'},
    {field: 'emergency_contact_country_id', label: 'fields.country'},
];

export default {
    name: 'Seafarer',

    components: {
        SeafarerApplications,
        SeafarerPlacements,
        UploadImage,
        DatePicker,
        CheckBox,
        InputField,
        Dropdown,
        Dropdown2,
        TextArea,
        Upload,
        SeafarerAdditional,
        SeafarerNavigation,
        SeafarerDocuments,
        SeafarerDocumentsArchive,
        SeafarerActivity,
        TheTable,
        ImageViewer,
        SeafarerExpensesTab,
        SeafarerSalaryTab,
    },

    data() {
        return {
            // ── Image lightbox ────────────────────────────────────────────────
            imageList: [],
            lightboxShow: false,
            lighboxIndex: 0,

            // ── Profile mini activity table ───────────────────────────────────
            activitiesTable: {
                selectedHeaders: [],
                // type: '' required on every header — TheTable.resetFilters() crashes otherwise
                headers: [
                    {
                        text: this.$t('fields.activityStatus'),
                        value: 'status',
                        type: '',
                        sortable: false,
                        allowFilter: false,
                    },
                    {
                        text: this.$t('fields.startDate'),
                        value: 'start_date',
                        type: '',
                        sortable: false,
                        allowFilter: false,
                    },
                    {text: this.$t('fields.endDate'), value: 'end_date', type: '', sortable: false, allowFilter: false},
                    {
                        text: this.$t('fields.duration'),
                        value: 'duration',
                        type: '',
                        sortable: false,
                        allowFilter: false,
                    },
                    {text: this.$t('fields.dayType'), value: 'day_type', type: '', sortable: false, allowFilter: false},
                    {text: this.$t('fields.vessel'), value: 'vessel', type: '', sortable: false, allowFilter: false},
                    {text: this.$t('fields.rank'), value: 'rank', type: '', sortable: false, allowFilter: false},
                    {text: this.$t('fields.entity'), value: 'entity', type: '', sortable: false, allowFilter: false},
                    {
                        text: this.$t('fields.placement'),
                        value: 'placement',
                        type: '',
                        sortable: false,
                        allowFilter: false,
                    },
                    {
                        text: this.$t('fields.contractId'),
                        value: 'contract',
                        type: '',
                        sortable: false,
                        allowFilter: false,
                    },
                    {text: this.$t('fields.note'), value: 'note', type: '', sortable: false, allowFilter: false},
                    {
                        text: this.$t('fields.modifiedBy'),
                        value: 'modified_by',
                        type: '',
                        sortable: false,
                        allowFilter: false,
                    },
                ],
                items: [],
                total: 0,
                height: '150px',
                defaultSort: {field: 'status', descending: true},
            },

            // ── Seafarer model ────────────────────────────────────────────────
            seafarer: new Seafarer({id: this.$route.params.id}),
            seafarerValidation: new Seafarer({}, true),

            // ── Placement IDs ─────────────────────────────────────────────────
            plannedPlacementId: null,
            contractPlacementId: null,

            // ── Edit mode / lock ──────────────────────────────────────────────
            inEditMode: false,
            lockKey: 'seafarer',
            lockTimer: null,
            initialLoad: false,

            // ── UI state ──────────────────────────────────────────────────────
            publicPath: process.env.BASE_URL,
            age: '',
            oibReadOnly: true,
            tabMain: 'seafarer-profile',
            tab: 'seafarer-profile',
            currentTab: 'seafarer-profile',
            // Additional tab javlja ima li nespremljenih izmjena — bez njih se ne pita za napuštanje.
            additionalDirty: false,
            reload: 0,
            statusReadOnly: true,
            approvedReadOnly: false,
            hadStatus: false,
            emailVerified: true,
            bankSyncRequired: false,
            bankOrigin: null,
            windowWidth: window.innerWidth,
            exportingCv: false,
            gdprAnoning: false,
            showOtpModal: false,
            otp: null,
            enteredOtp: '',

            // ── Status lists ──────────────────────────────────────────────────
            statuses: [
                {value: 1, label: 'Inactive (NC)'},
                {value: 2, label: 'Sick Leave - No contract (NC)'},
                {value: 3, label: 'Available (NC)'},
                {value: 4, label: 'On Leave (NC)'},
            ],
            statusesAll: [
                {value: 1, label: 'Inactive (NC)'},
                {value: 2, label: 'Sick Leave - No contract (NC)'},
                {value: 3, label: 'Available (NC)'},
                {value: 4, label: 'On Leave (NC)'},
                {value: 5, label: 'Travel to Vessel (C)'},
                {value: 6, label: 'Sick Leave - Contract (C)'},
                {value: 7, label: 'Training/Visa/etc. (C)'},
                {value: 8, label: 'On Board (C)'},
                {value: 9, label: 'Travel from Vessel (C)'},
            ],

            // ── Dynamic labels (change when approved state changes) ───────────
            nameLabel: this.$t('fields.name'),
            relationshipLabel: this.$t('fields.relationship'),
            addressLabel: this.$t('fields.address'),
            countryLabel: this.$t('fields.country'),
            contactNumberLabel: this.$t('fields.contactNumber'),
            contactEmailLabel: this.$t('fields.contactEmail'),
            bankNameLabel: this.$t('fields.bankName'),
            bankAddressLabel: this.$t('fields.bankAddress'),
            bankCityLabel: this.$t('fields.bankCity'),
            bankPostCodeLabel: this.$t('fields.bankPostCode'),
            bankCountryLabel: this.$t('fields.bankCountry'),
            swiftCodeLabel: this.$t('fields.swiftCode'),
            ibanLabel: this.$t('fields.iban'),
            accountCurrencyLabel: this.$t('fields.accountCurrency'),
            statusLabel: this.$t('fields.status'),
            rankLabel: this.$t('fields.rank'),
        };
    },

    computed: {
        verifyingGdprIsActive() {
            return this.enteredOtp.length !== 4;
        },
        bankDropdownCols() {
            return this.windowWidth <= 1636 ? 12 : 9;
        },
        availableStatuses() {
            // Once a seafarer has a status, show all statuses (including contract ones)
            return this.seafarer.status_id != null ? this.statusesAll : this.statuses;
        },
    },

    watch: {
        'seafarer.approved': {
            handler(approved) {
                this.checkStatusReadOnly();
                const prefix = approved ? '* ' : '';
                this.statusLabel = prefix + this.$t('fields.status');
                this.rankLabel = prefix + this.$t('fields.rank');
                this.nameLabel = prefix + this.$t('fields.name');
                this.relationshipLabel = prefix + this.$t('fields.relationship');
                this.addressLabel = prefix + this.$t('fields.address');
                this.countryLabel = prefix + this.$t('fields.country');
                this.contactNumberLabel = prefix + this.$t('fields.contactNumber');
                this.accountCurrencyLabel = prefix + this.$t('fields.accountCurrency');
            },
            deep: true,
        },

        'seafarer.photo': {
            handler(photo) {
                // BUG FIX: original code crashed when photo was null
                if (!photo || !photo.original_name) return;
                const ext = photo.original_name.split('.').pop().toLowerCase();
                if (!['jpg', 'jpeg', 'png', ''].includes(ext)) {
                    this.seafarer.photo.name = '';
                    this.seafarer.photo.link = '';
                    this.seafarer.photo.original_name = '';
                    this.seafarer.photo.upload_id = null;
                    this.$alert(this.$t('alerts.onlyPhoto'));
                }
            },
            deep: true,
        },

        // Recalculate overall when any rating changes
        'seafarer.perfomance'() {
            this.calculateOverall();
        },
        'seafarer.attitude'() {
            this.calculateOverall();
        },
        'seafarer.communication'() {
            this.calculateOverall();
        },

        'seafarer.date_of_birth'(dob) {
            if (!dob) return;
            const birth = new Date(dob);
            const today = new Date();
            const age =
                today.getFullYear() -
                birth.getFullYear() -
                (today.getMonth() < birth.getMonth() ||
                (today.getMonth() === birth.getMonth() && today.getDate() < birth.getDate())
                    ? 1
                    : 0);
            this.age = age;
            this.seafarerValidation.date_of_birth = age >= 18;
        },

        'seafarer.nationality_id'(id) {
            if (id === NATIONALITY_HR_ID) {
                this.oibReadOnly = false;
            } else {
                this.oibReadOnly = true;
                this.seafarer.oib = ''; // BUG FIX: was `==` (comparison) instead of `=` (assignment)
            }
        },

        'seafarer.name'() {
            this.populateAccountHolderName();
        },
        'seafarer.surname'() {
            this.populateAccountHolderName();
        },
    },

    beforeRouteLeave(to, from, next) {
        if (!this.inEditMode) {
            this.restoreHtmlScroll();
            next(true);
            return;
        }
        const leave = () => {
            this.restoreHtmlScroll();
            if (this.lockTimer !== null) clearInterval(this.lockTimer);
            this.$removeLock(this, this.lockKey + '|' + this.seafarer.id);
            next(true);
        };
        if (!this.needsLeaveConfirm()) {
            leave();
            return;
        }
        this.$verify(this.$t('alerts.leaveQuestion'), (result) => {
            if (result) {
                leave();
            } else {
                next(false);
            }
        });
    },

    beforeRouteUpdate(to, from, next) {
        // Ruta /seafarers/:id/:tab? dijeli instancu; pri promjeni id-a Vue reciklira instancu
        // (mounted se ne ponavlja) pa this.seafarer ostane zastario. Re-fetch samo kad se id promijeni;
        // promjena samo taba se rješava kroz handleInitialTabRedirects/tab logiku.
        if (to.params.id === from.params.id) {
            next();
            return;
        }
        this.seafarer = new Seafarer({id: to.params.id});
        this.initialLoad = false;
        if (this.seafarer.id != 0) {
            this.fetchSeafarer(() => {
                this.bankOrigin = this.seafarer.bank_id;
                this.$nextTick(() => {
                    this.initialLoad = true;
                    this.handleInitialTabRedirects();
                });
            });
        }
        next();
    },
    mounted() {
        if (!this.$myStore.state.loggedIn) {
            this.$router.replace('login');
            return;
        }

        window.addEventListener('resize', this.handleResize);
        document.getElementsByTagName('html')[0].style.overflowY = 'hidden';

        if (this.seafarer.id != 0) {
            this.fetchSeafarer(() => {
                this.bankOrigin = this.seafarer.bank_id;
                this.$nextTick(() => {
                    this.initialLoad = true;
                    this.handleInitialTabRedirects();
                });
            });
        } else {
            this.$myStore.commit('setBreadcrumbs', [
                {text: this.$t('modules.seafarers'), href: '/#/seafarers', disabled: false},
                {text: this.$t('modules.newSeafarer'), href: '/#/seafarers/0', disabled: true},
            ]);
            this.inEditMode = true;
            this.initialLoad = true;
        }

        this.checkApprovedReadOnly();
        this.checkStatusReadOnly();
    },

    beforeDestroy() {
        window.removeEventListener('resize', this.handleResize);
        // Očisti lock timer i (ako smo u editu) sam lock, za slučaj destroya izvan route-leave guarda.
        if (this.lockTimer !== null) clearInterval(this.lockTimer);
        if (this.inEditMode) this.$removeLock(this, `${this.lockKey}|${this.seafarer.id}`);
    },

    methods: {
        // ── Helpers ────────────────────────────────────────────────────────

        statusStyle(statusId) {
            return STATUS_STYLES[statusId] || {textColor: '#000000', color: '#eeeeee'};
        },

        restoreHtmlScroll() {
            document.getElementsByTagName('html')[0].style.overflowY = 'auto';
        },

        handleResize() {
            this.windowWidth = window.innerWidth;
        },

        populateAccountHolderName() {
            this.seafarer.bank_acccount_holder_name =
                this.seafarer.name && this.seafarer.surname ? `${this.seafarer.name} ${this.seafarer.surname}` : '';
        },

        /** BUG FIX: calculateOverall() previously returned NaN when all ratings were null. */
        calculateOverall() {
            const values = [this.seafarer.perfomance, this.seafarer.attitude, this.seafarer.communication].filter(
                (v) => v != null,
            );
            if (values.length === 0) return;
            this.seafarer.overall = parseFloat((values.reduce((a, b) => a + b, 0) / values.length).toFixed(1));
        },

        // ── Data fetching ──────────────────────────────────────────────────

        /**
         * Shared seafarer fetch used by both mounted() and reloadSeafarer().
         * Accepts an optional callback called after state is set.
         */
        fetchSeafarer(afterLoad) {
            const vm = this;
            this.$APICall('get', `seafarers/${this.seafarer.id}`, {}, (data, success) => {
                if (!success) return;
                const usable = JSON.parse(JSON.stringify(data));
                vm.seafarer = usable.object;
                vm.plannedPlacementId = usable.placement_planned_id;
                vm.contractPlacementId = usable.contract_placement_id;
                if (vm.seafarer.status_id != null) vm.hadStatus = true;
                if (vm.seafarer.photo != null) vm.imageList = [vm.seafarer.photo.link];
                vm.$myStore.commit('setBreadcrumbs', [
                    {text: vm.$t('modules.seafarers'), href: '/#/seafarers', disabled: false},
                    {
                        text: `${vm.seafarer.name} ${vm.seafarer.surname}`,
                        href: `/#/seafarers/${vm.seafarer.id}`,
                        disabled: true,
                    },
                ]);
                if (afterLoad) afterLoad(data);
            });
        },

        reloadSeafarer() {
            this.fetchSeafarer();
        },

        reloadData(request) {
            const vm = this;
            this.$APICall(
                'get',
                `seafarer-activity-for-profile/${this.seafarer.id}`,
                request,
                (data, success) => {
                    if (success) {
                        vm.activitiesTable.items = data;
                        // API vraća polje aktivnosti; total = broj redaka (0 ako nije polje).
                        vm.activitiesTable.total = Array.isArray(data) ? data.length : 0;
                    } else {
                        vm.$alert(vm.$t('alerts.basicError'));
                    }
                },
                false,
            );
        },

        selectHeaders(headers) {
            this.activitiesTable.selectedHeaders = headers;
        },

        // ── Tab navigation ─────────────────────────────────────────────────

        /**
         * BUG FIX: original code set `this.tabMain = this.currentTab` as first
         * statement, which reverted the tab before the guard ran.
         * Now the guard runs first; tab state is only updated on confirmed navigation.
         */
        changeTab(id) {
            if (!this.inEditMode) {
                this.applyTabChange(id);
                return;
            }
            // Klik na već otvoreni tab ne izlazi iz edit moda.
            if (id === this.currentTab) return;
            const leave = () => {
                clearInterval(this.lockTimer);
                this.$removeLock(this, this.lockKey + '|' + this.seafarer.id);
                this.inEditMode = false;
                this.applyTabChange(id);
            };
            if (!this.needsLeaveConfirm()) {
                leave();
                return;
            }
            this.$verify(this.$t('alerts.leaveQuestion'), (result) => {
                if (result) leave();
            });
        },

        // Tabovi bez praćenja izmjena uvijek pitaju; Additional samo kad stvarno ima nespremljenih izmjena.
        needsLeaveConfirm() {
            if (this.currentTab === 'additional') return this.additionalDirty;
            return true;
        },

        applyTabChange(id) {
            this.tabMain = id;
            this.tab = id;
            this.currentTab = id;
            this.lockKey = TAB_LOCK_KEYS[id] || id;
            if (id === 'seafarer-profile') {
                this.reloadData({});
                this.reloadSeafarer();
            }
        },

        handleInitialTabRedirects() {
            if (this.$myStore.state.openActivity) {
                this.changeTab('activity');
                this.$myStore.commit('setOpenActivity', false);
            } else if (this.$route.params.tab == 1) {
                this.changeTab('activity');
            } else if (this.$route.params.tab == 2) {
                this.changeTab('documents');
            }
            if (this.$myStore.state.openDocuments) {
                this.changeTab('documents');
                this.$myStore.commit('setOpenDocuments', false);
            }
        },

        // ── Edit mode / lock ───────────────────────────────────────────────

        editClick() {
            const vm = this;
            this.$setLock(this, `${this.lockKey}|${this.seafarer.id}`, (data) => {
                if (data.success) {
                    vm.inEditMode = true;
                    vm.lockTimer = setInterval(() => {
                        vm.$setLock(vm, `${vm.lockKey}|${vm.seafarer.id}`);
                    }, 10000);
                    vm.checkApprovedReadOnly();
                    vm.checkStatusReadOnly();
                } else {
                    vm.$alert(
                        this.$t('alerts.recordLocked')
                            .split('%name%')
                            .join(`${data.locked_by.first_name} ${data.locked_by.last_name}`),
                    );
                }
            });
        },

        editForTab() {
            if (this.inEditMode) {
                clearInterval(this.lockTimer);
                this.$removeLock(this, `${this.lockKey}|${this.seafarer.id}`);
            } else {
                this.editClick();
            }
            this.inEditMode = !this.inEditMode;
        },

        checkStatusReadOnly() {
            if (this.inEditMode) {
                if (!this.hadStatus) {
                    if (this.seafarer.approved == true) {
                        this.statusReadOnly = false;
                    } else {
                        this.seafarer.status_id = null;
                        this.statusReadOnly = true;
                    }
                }
            } else {
                this.statusReadOnly = true;
            }
        },

        checkApprovedReadOnly() {
            if (!this.inEditMode) {
                this.approvedReadOnly = true;
                return;
            }
            // New seafarers can always change approved; existing approved ones cannot un-approve
            this.approvedReadOnly = this.seafarer.id != 0 && this.seafarer.approved === true;
        },

        // ── Navigation ─────────────────────────────────────────────────────

        backClick() {
            this.$router.go(-1);
        },

        openPlacement() {
            this.$router.push('/placements/' + this.plannedPlacementId);
        },

        openContractPlacement() {
            this.$router.push('/placements/' + this.contractPlacementId);
        },

        copyFromAddresClick() {
            this.seafarer.emergency_contact_street_name_and_number = this.seafarer.street_name_and_number;
            this.seafarer.emergency_contact_post_code = this.seafarer.post_code;
            this.seafarer.emergency_contact_city = this.seafarer.city;
            this.seafarer.emergency_contact_country_id = this.seafarer.country_id;
        },

        // ── Save / validation ──────────────────────────────────────────────

        /**
         * Validates a single field. Sets validation state and appends to missingFields.
         * Returns true if valid.
         */
        validateField(value, validationKey, labelKey, missingFields) {
            const isEmpty = value == null || value === '';
            this.seafarerValidation[validationKey] = !isEmpty;
            if (isEmpty) missingFields.push(this.$t(labelKey));
            return !isEmpty;
        },

        saveClick(goBack) {
            let valid = true;
            const missing = [];

            // Approved-only required fields
            if (this.seafarer.approved) {
                this.validateField(this.seafarer.status_id, 'status_id', 'fields.status', missing) || (valid = false);
                this.validateField(this.seafarer.rank_id, 'rank_id', 'fields.rank', missing) || (valid = false);
            } else {
                this.seafarerValidation.status_id = true;
            }

            // Always required
            const alwaysRequired = [
                {val: this.seafarer.name, key: 'name', label: 'fields.firstName'},
                {val: this.seafarer.surname, key: 'surname', label: 'fields.lastName'},
                {val: this.seafarer.nationality_id, key: 'nationality_id', label: 'fields.nationality'},
                {
                    val: this.seafarer.country_of_residency_id,
                    key: 'country_of_residency_id',
                    label: 'fields.countryOfResidency',
                },
                {val: this.seafarer.place_of_birth, key: 'place_of_birth', label: 'fields.placeOfBirth'},
                {val: this.seafarer.marital_status_id, key: 'marital_status_id', label: 'fields.maritalStatus'},
                {val: this.seafarer.phone, key: 'phone', label: 'fields.phone'},
                {
                    val: this.seafarer.street_name_and_number,
                    key: 'street_name_and_number',
                    label: 'fields.streetNameAndNumber',
                },
                {val: this.seafarer.city, key: 'city', label: 'fields.city'},
                {val: this.seafarer.post_code, key: 'post_code', label: 'fields.postalCode'},
                {val: this.seafarer.country_id, key: 'country_id', label: 'fields.country'},
                {val: this.seafarer.airport_id, key: 'airport_id', label: 'fields.closestIntAirport'},
            ];
            alwaysRequired.forEach(({val, key, label}) => {
                if (!this.validateField(val, key, label, missing)) valid = false;
            });

            // Date of birth
            if (!this.seafarer.date_of_birth) {
                this.seafarerValidation.date_of_birth = false;
                missing.push(this.$t('fields.dateOfBirth'));
                valid = false;
            } else if (!this.seafarerValidation.date_of_birth) {
                // Already set to false by the watcher (under-18 check)
                valid = false;
            }

            // Email
            if (!this.$checkEmail(this.seafarer.email)) {
                this.seafarerValidation.email = false;
                missing.push(this.$t('fields.email'));
                valid = false;
            } else {
                this.seafarerValidation.email = true;
            }

            // OIB (Croatian nationals only)
            if (this.seafarer.nationality_id === NATIONALITY_HR_ID) {
                if (!this.seafarer.oib) {
                    this.seafarerValidation.oib = false;
                    missing.push(this.$t('fields.oib'));
                    valid = false;
                } else if (!this.$checkOIB(this.seafarer.oib)) {
                    this.seafarerValidation.oib = false;
                    missing.push(this.$t('alerts.oibFormattFailed'));
                    valid = false;
                } else {
                    this.seafarerValidation.oib = true;
                }
            } else {
                this.seafarerValidation.oib = true;
            }

            // Bank sync
            if (this.bankSyncRequired) {
                this.seafarerValidation.bank_id = false;
                missing.push(this.$t('alerts.syncBankData'));
                valid = false;
            } else {
                this.seafarerValidation.bank_id = true;
            }

            // Approved-only: account currency + NOK fields
            if (this.seafarer.approved) {
                if (!this.seafarer.account_currency_id) {
                    this.seafarerValidation.account_currency_id = false;
                    missing.push(this.$t('fields.accountCurrency'));
                    valid = false;
                } else {
                    this.seafarerValidation.account_currency_id = true;
                }

                APPROVED_REQUIRED_STRING_FIELDS.forEach(({field, label}) => {
                    if (!this.validateField(this.seafarer[field], field, label, missing)) valid = false;
                });
                APPROVED_REQUIRED_ID_FIELDS.forEach(({field, label}) => {
                    if (!this.validateField(this.seafarer[field], field, label, missing)) valid = false;
                });

                if (this.seafarer.contact_email && !this.$checkEmail(this.seafarer.contact_email)) {
                    this.seafarerValidation.contact_email = false;
                    missing.push(this.$t('fields.contactEmail'));
                    valid = false;
                } else {
                    this.seafarerValidation.contact_email = true;
                }
            }

            if (!valid) {
                if (missing.length) this.$alert(`${this.$t('alerts.missingFields')}<br/>${missing.join('<br/>')}`);
                return;
            }

            // FE-05: jedinstvenost emaila se provjeri prije spremanja; spremi tek u callbacku.
            this.checkEmailExistance(this.seafarer.email, () => this.persist(goBack));
        },

        persist(goBack) {
            const action = this.seafarer.id != 0 ? 'put' : 'post';
            const endpoint = `seafarers${this.seafarer.id != 0 ? '/' + this.seafarer.id : ''}`;
            this.bankOrigin = this.seafarer.bank_id;

            this.$APICall(action, endpoint, this.seafarer, (data, success) => {
                if (!success) {
                    this.$alert(this.$t('alerts.basicError'));
                    return;
                }
                this.inEditMode = false;
                this.$notify(this.$t(this.seafarer.id == 0 ? 'alerts.basicAdd' : 'alerts.basicUpdate'));
                clearInterval(this.lockTimer);
                this.$removeLock(this, `${this.lockKey}|${this.seafarer.id}`);
                if (goBack) this.backClick();
            });
        },

        // ── Email validation ───────────────────────────────────────────────

        checkEmailExistance(email, onValid) {
            if (!this.inEditMode || !email) return;
            this.$APICall(
                'get',
                `seafarers-email-verification/${this.$route.params.id}/${email}`,
                {},
                (data, success) => {
                    if (success) {
                        this.seafarerValidation.email = true;
                        this.emailVerified = true;
                        if (onValid) onValid();
                    } else {
                        this.emailVerified = false;
                        this.seafarerValidation.email = false;
                        this.$alert(Object.hasOwn(data, 'custom') ? data.message : this.$t('alerts.basicError'));
                    }
                },
                false,
            );
        },

        // ── Bank ───────────────────────────────────────────────────────────

        handleBankChange() {
            this.bankSyncRequired = this.seafarer.bank_id !== this.bankOrigin;
        },

        syncBank() {
            this.seafarerValidation.bank_id = true;
            this.bankSyncRequired = false;
            if (!this.seafarer.bank_id) {
                Object.assign(this.seafarer, {
                    bank_name: '',
                    bank_address: '',
                    bank_town: '',
                    bank_post_code: '',
                    bank_country_id: '',
                    swift_code: '',
                });
                return;
            }
            this.$APICall('get', `banks/${this.seafarer.bank_id}`, {}, (data, success) => {
                if (!success) {
                    this.$alert(this.$t('alerts.basicError'));
                    return;
                }
                this.seafarer.bank_name = data.name;
                this.seafarer.bank_address = data.address;
                this.seafarer.bank_town = data.city;
                this.seafarer.bank_post_code = data.post_code;
                this.seafarer.bank_country_id = data.country_id;
                this.seafarer.swift_code = data.swift_code;
            });
        },

        // ── CV export ──────────────────────────────────────────────────────

        exportCV() {
            this.exportingCv = true;
            this.$APICall(
                'get',
                `seafarers-export-cv/${this.$route.params.id}`,
                {},
                (data, success) => {
                    if (success) {
                        const url = URL.createObjectURL(data);
                        const link = document.createElement('a');
                        link.href = url;
                        link.download = 'cv_export.pdf';
                        link.click();
                        URL.revokeObjectURL(url);
                    } else {
                        this.$alert(this.$t('alerts.basicError'));
                    }
                    this.exportingCv = false;
                },
                false,
                'blob',
            );
        },

        // ── GDPR anonymisation ─────────────────────────────────────────────

        generateOtp() {
            this.otp = Math.floor(1000 + Math.random() * 9000);
        },

        gdprAnonVerification() {
            if (this.contractPlacementId != null || this.plannedPlacementId != null) {
                this.$alert(this.$t('alerts.anonymizationDisabledContractOrPlanned'));
                return;
            }
            this.$verify(this.$t('alerts.doGdprAnonymization'), (result) => {
                if (!result) return;
                this.enteredOtp = '';
                this.generateOtp();
                this.showOtpModal = true;
            });
        },

        gdprAnonExecution() {
            if (!this.enteredOtp) {
                this.$alert(this.$t('alerts.codeIsNotEntered'));
                return;
            }
            if (String(this.otp) !== String(this.enteredOtp)) {
                this.$alert(this.$t('alerts.codeIsInvalid'));
                return;
            }
            this.gdprAnoning = true;
            this.$APICall('post', `seafarer-gdpr-anon/${this.$route.params.id}`, {}, (data, success) => {
                if (success) {
                    this.$alert(this.$t('alerts.seafarerGdprAnonymized'));
                    this.backClick();
                } else {
                    this.$alert(Object.hasOwn(data, 'custom') ? data.message : this.$t('alerts.basicError'));
                }
                this.gdprAnoning = false;
                this.showOtpModal = false;
            });
        },
    },
};
</script>

<style scoped>
.photoNoPlanned {
    height: 435px;
}
.photoPlanned {
    height: 390px;
}
</style>
